/**
 * Транзиентное движение слоя: «его тянут прямо сейчас».
 *
 * ЧТО ЭТО. Пока модератор держит мышь, панель шлёт положение слоя
 * до шестидесяти раз в секунду (docs/02). Hub рассылает это остальным
 * в комнате, исключая отправителя, — чтобы зритель видел движение,
 * а не телепортацию из точки в точку по отпускании кнопки.
 *
 * ЧЕМ ЭТО НЕ ЯВЛЯЕТСЯ. Не изменением сцены. Версия не растёт, в журнал
 * операций и в очередь записи не попадает ничего, отменить это нечем
 * и незачем: жест заканчивается настоящей операцией, она и запишется.
 * Поэтому транзиент НЕ ИДЁТ в состояние сцены — он живёт поверх него
 * и умирает, как только приходит настоящее положение.
 *
 * Разбор общий, как у стоп-крана: панель однажды тоже начнёт
 * показывать чужие движения, и вторая реализация того же разошлась бы
 * с первой молча.
 */
/**
 * Сколько живёт транзиент без продолжения.
 *
 * СТРАХОВКА ОТ ЗАЛИПШЕГО ПОЛОЖЕНИЯ. Обычно жест кончается настоящей
 * операцией, и та стирает транзиент. Но операцию могут и отклонить —
 * лиза отобрана, заморозка, разрыв версии, — а панель могла просто
 * закрыться посреди движения. Тогда последнее транзиентное положение
 * осталось бы в кадре навсегда: откатить его нечем, оно нигде
 * не сохранено.
 *
 * Две секунды — заведомо больше промежутка между кадрами жеста
 * (их до шестидесяти в секунду) и заведомо меньше, чем терпимо
 * смотреть на неправду в эфире.
 *
 * СРОК ОБЩИЙ ДЛЯ ОБОИХ КОНЦОВ, и потому живёт здесь, рядом
 * с разбором. Отправитель обязан знать, сколько его молчание терпят
 * (LIVE_KEEPALIVE_MS); разойдись эти два числа по своим файлам —
 * и разъехались бы молча, а слой пропадал бы из кадра посреди жеста.
 */
import { decodeMessage } from './message.js';
export const LIVE_TTL_MS = 2_000;
/**
 * Через сколько молчания отправитель повторяет последнее положение.
 *
 * ЖЕСТ — ЭТО НЕ ТОЛЬКО ДВИЖЕНИЕ. Держащий мышь замирает: целится,
 * смотрит на кадр, ждёт, пока доедет видео. Кадры жеста в это время
 * не идут — двигать нечего, — и смотрящий, дождавшись LIVE_TTL_MS,
 * забывал транзиент и возвращал слой туда, где тот записан в сцене.
 * Слой, занесённый в кадр из рабочей области, на глазах пропадал
 * из эфира и появлялся от одного шевеления мышью.
 *
 * Втрое чаще срока, а не «незадолго до конца», по тому же доводу,
 * что у продления лизы: одно потерянное сообщение — обычное дело
 * при перегруженной сети, и запас должен оставлять место второй
 * попытке.
 */
export const LIVE_KEEPALIVE_MS = Math.floor(LIVE_TTL_MS / 3);
/**
 * Разбирает сообщение `live`.
 *
 * ВОЗВРАЩАЕТ СПИСОК, а не одно положение: одно движение руки — одно
 * сообщение, сколько бы слоёв ни тянули. Выделяют и тянут пачкой,
 * и по сообщению на слой стоило бы отдельного кадра WebSocket,
 * отдельного разбора в hub, отдельной рассылки по комнате и отдельной
 * перерисовки у каждого получателя.
 *
 * ПОНИМАЕТ ОБЕ ФОРМЫ — список и одиночные поля. Не из всеядности:
 * во время выкатки старая панель и новый получатель работают вместе.
 * Список важнее: отправитель, заполнивший оба, имел в виду пачку,
 * а одиночные поля оставил для тех, кто списка не понимает.
 *
 * Возвращает `null`, если сообщение не о транзиенте ИЛИ если в нём
 * не осталось ни одного годного положения. Второе важно не меньше
 * первого: NaN приходит с клиента — деление на ноль в пересчёте
 * экранных координат даёт именно его, — а нарисованный им слой
 * не показать и не убрать, потому что откатить транзиент нечем:
 * он нигде не сохранён.
 */
export function parseLive(raw) {
    const message = decodeMessage(raw);
    if (message === null || message.t !== 'live')
        return null;
    const live = decodeMessage(message.live);
    if (live === null)
        return null;
    const entries = Array.isArray(live.layers) ? live.layers : [live];
    /*
      НЕГОДНАЯ ЗАПИСЬ ВЫБРАСЫВАЕТСЯ, А ОСТАЛЬНЫЕ ЕДУТ.
  
      Пока сообщений было по одному на слой, кривое значение губило
      ровно свой слой. Обрушить из-за него весь кадр значило бы сделать
      из мелкой ошибки крупную: у зрителя встало бы всё выделение,
      а не один слой.
    */
    const moves = [];
    for (const entry of entries) {
        const move = oneMove(entry);
        if (move !== null)
            moves.push(move);
    }
    return moves.length === 0 ? null : moves;
}
/** Одно положение из пачки. null — запись негодная. */
function oneMove(raw) {
    const entry = decodeMessage(raw);
    if (entry === null)
        return null;
    const layerId = entry.layerId;
    if (typeof layerId !== 'string' || layerId === '')
        return null;
    const t = decodeMessage(entry.transform);
    if (t === null)
        return null;
    const x = finite(t.x);
    const y = finite(t.y);
    const w = finite(t.w);
    const h = finite(t.h);
    if (x === null || y === null || w === null || h === null)
        return null;
    // Размеры строго положительные: нулевой или отрицательный
    // прямоугольник нельзя нарисовать, а слой с ним пропал бы из кадра
    // навсегда — вернуть его было бы нечем
    if (w <= 0 || h <= 0)
        return null;
    return {
        layerId,
        transform: {
            x,
            y,
            w,
            h,
            // Поворот и прозрачность необязательны: сообщение без них
            // означает «как есть», а не «сбросить в ноль». Ноль
            // прозрачности сделал бы слой невидимым посреди жеста
            rotation: finite(t.rotation) ?? 0,
            opacity: finite(t.opacity) ?? 1,
        },
    };
}
/** Число, которое можно нарисовать: не NaN и не бесконечность. */
function finite(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
//# sourceMappingURL=live.js.map